import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle2, X, ChevronLeft, Shield, Clock, Heart, Phone, Mail, User, MessageSquare } from 'lucide-react';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';

const quoteSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name').max(100),
  email: z.string().trim().email('Please enter a valid email').max(255),
  phone: z.string().trim().min(7, 'Please enter your phone number').max(20),
  service: z.string().min(1, 'Please pick a service'),
  message: z.string().trim().min(10, 'Tell us a bit more about your project').max(5000),
});

const serviceOptions = [
  'Website / Web App',
  'Mobile App',
  'AI & Automation',
  'Digital Marketing & SEO',
  'E-commerce Store',
  'UI/UX Design',
  'Other',
];

interface QuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

export const QuoteModal = ({ open, onOpenChange, source }: QuoteModalProps) => {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', service: '', message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      // Auto-fill from profile
      const loadProfile = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email, phone')
          .eq('id', user.id)
          .single();
        if (profile) {
          setFormData(prev => ({
            ...prev,
            name: prev.name || profile.full_name || '',
            email: prev.email || profile.email || user.email || '',
            phone: prev.phone || profile.phone || '',
          }));
        }
      };
      loadProfile();
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = quoteSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      await supabase.functions.invoke('send-form-email', {
        body: {
          formKey: 'quote',
          formData: { ...formData, source: source || window.location.pathname },
          senderEmail: formData.email,
        },
      });
      setIsSuccess(true);
      toast.success("Got it! We'll be in touch within 24 hours.");
    } catch {
      setIsSuccess(true);
      toast.success("Got it! We'll be in touch within 24 hours.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    setErrors({});
    setIsSuccess(false);
    onOpenChange(false);
  };

  const formContent = (
    <div className={isMobile ? "px-4 py-5" : "p-5"}>
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Thank you! 🎉</h3>
            <p className="text-sm text-muted-foreground mb-6">
              We'll get back to you within 24 hours.
            </p>
            <Button onClick={resetAndClose} className="h-11 w-full active:scale-[0.97]">
              Done
            </Button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {/* Name */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Name <span className="text-destructive">*</span></label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                <Input
                  name="name"
                  autoComplete="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={`h-12 pl-10 text-base rounded-xl bg-background ${errors.name ? 'border-destructive' : ''}`}
                />
              </div>
              {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Email <span className="text-destructive">*</span></label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@email.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={`h-12 pl-10 text-base rounded-xl bg-background ${errors.email ? 'border-destructive' : ''}`}
                />
              </div>
              {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Phone <span className="text-destructive">*</span></label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                <Input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className={`h-12 pl-10 text-base rounded-xl bg-background ${errors.phone ? 'border-destructive' : ''}`}
                />
              </div>
              {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
            </div>

            {/* Service */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Service <span className="text-destructive">*</span></label>
              <Select value={formData.service} onValueChange={(v) => updateField('service', v)}>
                <SelectTrigger className={`h-12 text-base rounded-xl bg-background ${errors.service ? 'border-destructive' : ''}`}>
                  <SelectValue placeholder="Pick a service" />
                </SelectTrigger>
                <SelectContent position="popper" side="bottom" className="z-[300] bg-popover border shadow-xl max-h-[260px]">
                  {serviceOptions.map((s) => (
                    <SelectItem key={s} value={s} className="py-2.5 text-sm">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.service && <p className="text-xs text-destructive mt-1">{errors.service}</p>}
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Tell us more <span className="text-destructive">*</span>
              </label>
              <Textarea
                placeholder="What are you looking to build?"
                value={formData.message}
                onChange={(e) => updateField('message', e.target.value)}
                className={`min-h-[100px] text-base rounded-xl bg-background resize-none ${errors.message ? 'border-destructive' : ''}`}
              />
              {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full h-12 text-sm font-semibold rounded-xl shadow-md shadow-primary/15 active:scale-[0.97]"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Request'}
              {!isSubmitting && <Send className="w-4 h-4 ml-2" />}
            </Button>

            {/* Trust */}
            <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-primary/50" /> Confidential</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-primary/50" /> 24h reply</span>
              <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-primary/50" /> No spam</span>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );

  // Mobile: Drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(v) => { if (!v) resetAndClose(); else onOpenChange(v); }}>
        <DrawerContent className="h-[92vh] max-h-[92vh] overflow-hidden">
          <DrawerHeader className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <DrawerClose asChild>
              <button className="flex items-center gap-1 text-primary font-medium">
                <ChevronLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
            </DrawerClose>
            <DrawerTitle className="font-semibold flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              Get a Quote
            </DrawerTitle>
            <div className="w-12" />
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto">{formContent}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Modal
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={resetAndClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 m-auto z-[101] bg-card max-w-md w-[95%] h-fit rounded-2xl max-h-[90vh] overflow-hidden border border-border shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary" />
                <span className="font-semibold text-foreground text-sm">Get a Quote</span>
              </div>
              <button
                onClick={resetAndClose}
                className="w-8 h-8 rounded-full bg-muted/80 flex items-center justify-center hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-52px)]">{formContent}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ── Context & Provider ──

interface QuoteModalContextType {
  openQuoteModal: (srcOrEvent?: string | React.MouseEvent) => void;
}

const QuoteModalContext = createContext<QuoteModalContextType | undefined>(undefined);

export const QuoteModalProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<string | undefined>();

  const openQuoteModal = (srcOrEvent?: string | React.MouseEvent) => {
    const src = typeof srcOrEvent === 'string' ? srcOrEvent : undefined;
    setSource(src || window.location.pathname);
    setOpen(true);
  };

  return (
    <QuoteModalContext.Provider value={{ openQuoteModal }}>
      {children}
      <QuoteModal open={open} onOpenChange={setOpen} source={source} />
    </QuoteModalContext.Provider>
  );
};

export const useQuoteModal = () => {
  const context = useContext(QuoteModalContext);
  if (!context) throw new Error('useQuoteModal must be used within QuoteModalProvider');
  return context;
};
